import { ReactNode, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUsername } from "@/hooks/useUsername";

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();
  const { username, loading: usernameLoading } = useUsername();
  const location = useLocation();

  // Once we've resolved the username state at least once, remember it so
  // background revalidations don't blank the screen with "Loading…".
  const everResolvedRef = useRef(false);
  if (!usernameLoading) everResolvedRef.current = true;

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/auth/" replace />;

  // Username gate — required for all signed-in users in v1.
  // Already on the onboarding route? let it render so we don't loop.
  const onOnboarding = location.pathname.startsWith("/onboarding/username");

  // Only block on the very first resolution; subsequent revalidations are silent.
  if (usernameLoading && !everResolvedRef.current) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  // Only redirect to onboarding when we have a definitive answer (not loading).
  // Otherwise a transient `username=null` during a background revalidation
  // (e.g. WorkspaceProvider.refresh flipping wsLoading back to true) would
  // bounce the user to /onboarding and back, causing a flash loop.
  if (!usernameLoading && !username && !onOnboarding) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/onboarding/username?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
};
